<template>
  <v-container>
    <v-divider />
    <v-row>
      <v-col cols="12"><h4>{{ $t('title') }}</h4></v-col>
    </v-row>
    <v-row>
      <v-col cols="12">
        <v-text-field
          :label="$t('labelQuestionTitle')"
          v-model="title"
          @input="dirty = true"
          required
        >{{ $t('placeHolderQuestionTitle')}}</v-text-field>
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <v-switch v-model="displayAnswer" :label="$t('switchDisplayAnswer')" />
      </v-col>
    </v-row>
    <v-row>
      <v-col class="pt-5 pb-10" cols="12">
        <v-slider
          :label="$t('labelPoints')"
          v-model="points"
          thumb-label="always"
          :hint="$t('hintPoints')"
          persistent-hint
          @input="dirty = true"
        />
      </v-col>
    </v-row>
    <v-divider />
    <clues-question
      :questionId="question.id"
      :is-new-question="isNewQuestion"
      @clues="handleClues"
    />
    <v-divider class="mt-6" />
    <v-row>
      <v-col class="my-4" cols="12"><h4>{{ $t('titleResponses') }}</h4></v-col>
    </v-row>
    <v-row>
      <v-col cols="12">
        <v-alert type="info">
          {{ alertContent }}
        </v-alert>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12">
        <v-textarea
          v-if="question.type === 'open'"
          outlined
          :name="$t('nameHelp')"
          :label="$t('labelHelp')"
          v-model="expectedAnswer"
          :hint="$t('hintHelp')"
          persistent-hint
          @input="dirty = true"
        />
      </v-col>
    </v-row>
    <v-row>
      <v-col class="text-right">
        <v-btn color="primary" @click="removeQuestion">{{ $t('btnDeleteQuestion') }}</v-btn>&nbsp;
        <v-btn color="secondary" @click="saveQuestion">{{ $t('btnSaveQuestion') }}</v-btn>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { v4 as uuidv4 } from 'uuid';
import CluesQuestion from './CluesQuestion.vue';

export default {
  components: {
    CluesQuestion,
  },
  props: {
    question: {
      type: Object,
      required: false,
      default: null,
    },
    surveyId: {
      type: String,
      required: true,
    },
    isNewQuestion: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      dirty: false,
      id: uuidv4(),
      order: 1,
      points: 0,
      requiresReview: true,
      title: null,
      type: this.question.type,
      expectedAnswer: null,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      displayAnswer: false,
      clues: null,
    };
  },
  computed: {
    alertContent() {
      if (this.type === 'open') {
        return this.$t('alert');
      }
      return this.$t('alertTeamName');
    },
  },
  methods: {
    saveQuestion() {
      this.dirty = false;
      const q = {
        id: this.id,
        title: this.title,
        order: this.order,
        points: this.points,
        createdAt: this.createdAt,
        updatedAt: this.updatedAt,
        questionId: this.question.id ? this.question.id : uuidv4(),
        surveyId: this.surveyId,
        type: this.question.type,
        expectedAnswer: this.expectedAnswer,
        displayAnswer: this.displayAnswer,
      };
      if (this.clues !== null) {
        this.$emit('question-updated', q, this.clues);
      } else {
        this.$emit('question-updated', q, null);
      }
    },
    removeQuestion() {
      this.dirty = false;
      this.$emit('question-removed', this.id);
    },
    populateWith(question) {
      this.expectedAnswer = question.expectedAnswer;
      this.points = question.points;
      this.order = question.order;
      this.id = question.id;
      this.title = question.title;
      this.createdAt = question.createdAt;
      this.updatedAt = question.updatedAt;
      this.requiresReview = question.requiresReview;
      this.type = this.question.type;
      this.displayAnswer = question.displayAnswer;
    },
    handleClues(clues) {
      this.clues = clues;
    },
  },
  mounted() {
    if (this.question) {
      this.populateWith(this.question);
    }
  },
};
</script>
<i18n>
{
  "fr": {
    "title": "Paramètres d'une question à texte libre",
    "alert": "Pour ce type de question, le joueur répond par un texte libre. L'animateur devra corriger la réponse. L'aide à la correction permet de donner des informations au correcteur pour l'aider à corriger et ne sera pas affichée aux joueurs.",
    "alertTeamName": "Pour ce type de question, l'équipe doit taper un nom d'équipe qui sera adopté automatiquement. Attention, n'ajouter qu'une seule question de ce type par destination",
    "labelQuestionTitle": "Intitulé de la question",
    "placeHolderQuestionTitle": "Question",
    "labelPoints": "Points",
    "hintPoints": "Définissez le nombre de points gagnés en cas de réponse correcte",
    "nameHelp": "Aide à la correction",
    "labelHelp": "Aide à la correction",
    "hintHelp": "Tapez ici la réponse que vous attendez pour aider les correcteurs",
    "btnDeleteQuestion": "Supprimer",
    "btnSaveQuestion": "Enregistrer",
    "switchDisplayAnswer": "Afficher les réponses correctes une fois que les joueurs ont répondu (indoor uniquement)",
    "titleResponses": "Gestion des élements de réponses"
  },
  "en": {
    "title": "Open question parameter",
    "alert": "The player answers to this question by typing free text. A staff member will have to review the answer. The hint helps the staff for reviewing the answer and is not displayed to the players",
    "alertTeamName": "The team writes a team name that will be adopted automatically in the scoreboard. Warning: please add only one question of this type per destination.",
    "labelQuestionTitle": "Question title",
    "placeHolderQuestionTitle": "Question",
    "labelPoints": "Points",
    "hintPoints": "Define the number of points earned with a correct answer",
    "nameHelp": "Review hint",
    "labelHelp": "Review hint",
    "hintHelp": "Enter here the expected answer",
    "btnDeleteQuestion": "Delete",
    "btnSaveQuestion": "Save",
    "switchDisplayAnswer": "Display the expected answers when the players have answered (indoor only)",
    "titleResponses": "Response element management"
  }
}
</i18n>
